import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  Wifi, KeyRound, Waves, MessageCircleQuestion, CalendarHeart, MapPin, Phone, MessageSquareText,
  AlertTriangle, X, ChevronLeft, ChevronRight, Facebook, Instagram, BedDouble, Bath, Users, ExternalLink, LogOut,
} from "lucide-react";
import { getContent } from "../lib/api";
import { Logo, PebbleMark } from "../components/Logo";
import { SearchBox, buildSearchIndex } from "../components/SearchBox";
import { GuideSection } from "../components/GuideSections";
import { Concierge } from "../components/Concierge";

const scrollToEl = (el) => {
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -132, duration: 0.9 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const TILES = [
  { id: "wifi", label: "Wi-Fi", desc: "Network & password", icon: Wifi, bg: "#2A2521", type: "panel" },
  { id: "arrival-entry", label: "Arrival & entry", desc: "Gate, parking & getting in", icon: KeyRound, bg: "#5F7054", type: "panel" },
  { id: "pool-spa", label: "Pool & spa", desc: "iPad controls & rules", icon: Waves, bg: "#B4552A", type: "panel" },
  { id: "ask", label: "Ask the guide", desc: "AI concierge, day or night", icon: MessageCircleQuestion, bg: "#8A5A33", type: "dialog" },
  { id: "rebook", label: "Click to Rebook & Save 15%", desc: "Book direct for next time", icon: CalendarHeart, bg: "#C9923B", type: "link" },
];

export default function Guide() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);
  const [activePanel, setActivePanel] = useState(null);
  const [conciergeOpen, setConciergeOpen] = useState(false);
  const [conciergeSeed, setConciergeSeed] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const t = localStorage.getItem("pf_guest_token");
    let ok = false;
    if (t) {
      try { ok = JSON.parse(atob(t.split(".")[1])).exp * 1000 > Date.now(); } catch { ok = false; }
    }
    if (!ok) {
      localStorage.removeItem("pf_guest_token");
      navigate("/login");
    } else {
      setAuthed(true);
    }
  }, [navigate]);

  useEffect(() => {
    if (!authed) return;
    getContent().then(setContent).catch(() => setError("The guide couldn't load. Check your connection and try again."));
  }, [authed]);

  const openPanel = useCallback((id, scroll = true) => {
    setActivePanel(id);
    if (scroll) {
      requestAnimationFrame(() => setTimeout(() => scrollToEl(document.getElementById(id)), 80));
    }
  }, []);

  useEffect(() => {
    if (!content) return;
    const fromHash = () => {
      const id = window.location.hash.replace("#", "");
      if (id) openPanel(id);
    };
    fromHash();
    window.addEventListener("hashchange", fromHash);
    return () => window.removeEventListener("hashchange", fromHash);
  }, [content, openPanel]);

  const searchIndex = useMemo(() => (content ? buildSearchIndex(content) : []), [content]);

  const handleToggle = useCallback((id) => {
    setActivePanel((prev) => (prev === id ? null : id));
  }, []);

  const openSection = useCallback((sectionId) => {
    if (!content) return;
    if (sectionId === "local") {
      openPanel("local-water");
      return;
    }
    const section = content.sections.find((s) => s.id === sectionId);
    if (section?.panels?.length) openPanel(section.panels[0].id);
  }, [content, openPanel]);

  const signOut = () => {
    localStorage.removeItem("pf_guest_token");
    navigate("/login");
  };

  if (!authed) return null;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center" data-testid="guide-error">
          <PebbleMark size={48} />
          <p className="mt-4 text-soft">{error}</p>
          <button onClick={() => window.location.reload()} className="mt-4 bg-copper text-cream rounded-full px-6 py-3 font-semibold" data-testid="guide-reload-button">
            Reload the guide
          </button>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="guide-loading">
        <div className="animate-pulse flex flex-col items-center gap-3">
          <PebbleMark size={48} />
          <p className="text-soft font-serif italic">Setting out the welcome mat…</p>
        </div>
      </div>
    );
  }

  const p = content.property;
  const navItems = [...content.sections.map((s) => ({ id: s.id, label: s.nav }))];
  navItems.splice(6, 0, { id: "local", label: "Local guide" });
  const rebookLabel = p.rebook_offer_enabled ? p.rebook_offer_text : "Rebook Pebble Farms";

  return (
    <div id="top" className="bg-cream min-h-screen">
      {/* Sticky header */}
      <header className="sticky top-0 z-50 bg-cream/90 backdrop-blur-md border-b border-charcoal/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <a href={`tel:${p.phone_tel}`} className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-charcoal hover:text-copperdeep transition-colors px-3 py-2" data-testid="header-call-link">
              <Phone size={15} aria-hidden /> {p.phone_display}
            </a>
            <button
              onClick={() => setConciergeOpen(true)}
              className="inline-flex items-center gap-1.5 bg-charcoal text-cream text-sm font-bold rounded-full px-4 py-2.5 hover:bg-copperdeep transition-colors"
              data-testid="header-ask-guide-button"
            >
              <MessageCircleQuestion size={15} aria-hidden /> Ask the guide
            </button>
            <button onClick={signOut} className="p-2.5 rounded-full hover:bg-sand transition-colors text-soft hover:text-charcoal" aria-label="Sign out" data-testid="guide-sign-out-button">
              <LogOut size={17} />
            </button>
          </div>
        </div>
        <nav className="border-t border-charcoal/5" aria-label="Guide sections">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto py-2" data-testid="guide-sticky-nav">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => openSection(item.id)}
                className="whitespace-nowrap text-sm font-extrabold text-soft hover:text-charcoal hover:bg-sand rounded-full px-4 py-2 transition-colors"
                data-testid={`nav-${item.id}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative" aria-label="Welcome">
        <div className="relative h-[62vh] sm:h-[72vh] overflow-hidden">
          <motion.img
            src={content.photos.cover}
            alt="Pebble Farms — resort pool and Hill Country grounds"
            className="absolute inset-0 w-full h-full object-cover"
            initial={reduceMotion ? false : { scale: 1.14 }}
            animate={{ scale: 1 }}
            transition={{ duration: 3.2, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-cover-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/35 to-charcoal/20" aria-hidden />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-10 sm:pb-14 w-full">
              <motion.p
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.6 }}
                className="text-cream/85 uppercase tracking-[0.24em] text-xs sm:text-sm font-semibold mb-3"
                data-testid="hero-location"
              >
                <MapPin size={13} className="inline -mt-0.5 mr-1" aria-hidden />
                {p.address} · {p.city_state_zip}
              </motion.p>
              <h1 className="font-serif text-cream font-semibold leading-[1.02]" data-testid="hero-headline">
                <span className="mask-line">
                  <motion.span
                    className="block text-2xl sm:text-3xl italic font-medium text-cream/90"
                    initial={reduceMotion ? false : { y: "110%" }} animate={{ y: 0 }}
                    transition={{ delay: 0.35, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {content.hero.headline_top}
                  </motion.span>
                </span>
                <span className="mask-line">
                  <motion.span
                    className="block text-5xl sm:text-7xl"
                    initial={reduceMotion ? false : { y: "110%" }} animate={{ y: 0 }}
                    transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {content.hero.headline_name}
                  </motion.span>
                </span>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
                className="text-cream/90 text-lg sm:text-xl mt-4 max-w-xl"
                data-testid="hero-subheadline"
              >
                {content.hero.sub}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.7 }}
                className="flex flex-wrap gap-2 mt-5"
                data-testid="hero-highlights"
              >
                <span className="inline-flex items-center gap-1.5 bg-cream/15 backdrop-blur border border-cream/25 text-cream text-sm font-semibold rounded-full px-3.5 py-1.5">
                  <BedDouble size={14} aria-hidden /> {p.bedrooms} bedrooms
                </span>
                <span className="inline-flex items-center gap-1.5 bg-cream/15 backdrop-blur border border-cream/25 text-cream text-sm font-semibold rounded-full px-3.5 py-1.5">
                  <Bath size={14} aria-hidden /> {p.bathrooms} bathrooms
                </span>
                <span className="inline-flex items-center gap-1.5 bg-cream/15 backdrop-blur border border-cream/25 text-cream text-sm font-semibold rounded-full px-3.5 py-1.5">
                  <Users size={14} aria-hidden /> Sleeps {p.occupancy}
                </span>
                {content.hero.highlights.slice(0, 3).map((h) => (
                  <span key={h} className="hidden sm:inline-flex bg-cream/15 backdrop-blur border border-cream/25 text-cream text-sm font-semibold rounded-full px-3.5 py-1.5">{h}</span>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Search + quick actions */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 -mt-8 relative z-10" aria-label="Search and quick actions">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.6 }}>
          <SearchBox
            index={searchIndex}
            onSelect={(id) => openPanel(id)}
            onAskConcierge={(q) => { setConciergeSeed(q); setConciergeOpen(true); }}
          />
        </motion.div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-5" data-testid="quick-actions">
          {TILES.map((tile, i) => {
            const inner = (
              <>
                <tile.icon size={24} className="text-white mb-2.5 group-hover:scale-110 transition-transform" aria-hidden />
                <span className="block font-extrabold text-white leading-tight">{tile.id === "rebook" ? rebookLabel : tile.label}</span>
                <span className="block text-sm text-white/75 mt-1">{tile.desc}</span>
              </>
            );
            const cls = "rounded-2xl p-4 sm:p-5 text-left transition-all group hover:scale-[1.03] hover:shadow-lg";
            const style = { background: tile.bg };
            const motionProps = {
              initial: reduceMotion ? false : { opacity: 0, y: 18 },
              animate: { opacity: 1, y: 0 },
              transition: { delay: 1.15 + i * 0.08, duration: 0.5 },
            };
            if (tile.type === "link") {
              return (
                <motion.a key={tile.id} {...motionProps} href={p.booking_url} target="_blank" rel="noreferrer"
                  className={cls} style={style} data-testid="quick-action-rebook">
                  {inner}
                </motion.a>
              );
            }
            return (
              <motion.button key={tile.id} {...motionProps}
                onClick={() => (tile.type === "dialog" ? setConciergeOpen(true) : openPanel(tile.id))}
                className={cls} style={style} data-testid={`quick-action-${tile.id}`}>
                {inner}
              </motion.button>
            );
          })}
        </div>
      </section>

      {/* Safety banner */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-10" aria-label="Important safety notice">
        <motion.button
          initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          onClick={() => openPanel("child-safety")}
          className="w-full text-left bg-[#FBF1EA] border-2 border-copper/50 rounded-2xl px-5 sm:px-6 py-5 flex items-start gap-4 hover:border-copper transition-colors"
          data-testid="safety-banner"
        >
          <AlertTriangle size={24} className="text-copper shrink-0 mt-0.5" aria-hidden />
          <span>
            <span className="block font-extrabold text-lg text-copperdeep">Important pool & child safety</span>
            <span className="block text-soft mt-1">{content.safety_alert}</span>
          </span>
        </motion.button>
      </section>

      {/* Gallery */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-16" aria-label="Photo gallery" data-testid="gallery-section">
        <div className="flex items-baseline gap-4 mb-6">
          <span className="font-serif italic text-copper text-xl" aria-hidden>✳</span>
          <h2 className="font-sans text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-charcoal">Around the farm</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 auto-rows-[140px] sm:auto-rows-[180px] gap-3">
          {content.photos.gallery.slice(0, 7).map((photo, i) => (
            <motion.button
              key={photo.url}
              initial={reduceMotion ? false : { opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.07 }}
              onClick={() => setLightbox(i)}
              className={`relative overflow-hidden rounded-2xl group ${i === 0 ? "col-span-2 row-span-2" : ""} ${i === 3 ? "sm:col-span-2" : ""}`}
              data-testid={`gallery-item-${i}`}
              aria-label={`View photo: ${photo.label}`}
            >
              <img src={photo.url} alt={photo.label} loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-charcoal/70 to-transparent p-3 text-left">
                <span className="text-cream text-sm font-semibold">{photo.label}</span>
              </span>
            </motion.button>
          ))}
        </div>
        <p className="text-xs text-soft mt-3">{content.photos.credit}</p>
      </section>

      {/* Guide sections */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 mt-16 space-y-16">
        {content.sections.map((section, i) => (
          <GuideSection
            key={section.id}
            section={section}
            index={i}
            activePanel={activePanel}
            onToggle={handleToggle}
            wifi={content.wifi}
            onOpenConcierge={() => setConciergeOpen(true)}
          />
        ))}

        {/* Local guide */}
        <section id="section-local" className="scroll-mt-32" aria-label="Local guide" data-testid="section-local">
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55 }}
            className="flex items-baseline gap-4 mb-4"
          >
            <span className="font-serif italic text-copper text-xl" aria-hidden>{String(content.sections.length + 1).padStart(2, "0")}</span>
            <h2 className="font-sans text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-charcoal">Local guide</h2>
          </motion.div>
          <p className="text-soft text-base leading-relaxed mb-8 max-w-3xl">{content.local_guide.intro}</p>
          <div className="space-y-8">
            {content.local_guide.categories.map((cat) => (
              <div key={cat.id} id={`local-${cat.id}`} className="scroll-mt-40" data-testid={`local-cat-${cat.id}`}>
                <h3 className="font-serif text-xl font-semibold text-sagedeep mb-3">{cat.title}</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {cat.items.map((item) => (
                    <div key={item.name} className="bg-white border border-charcoal/10 rounded-2xl p-5 hover:shadow-md transition-shadow">
                      <p className="font-semibold text-charcoal text-lg">{item.name}</p>
                      <p className="text-soft text-base mt-1 leading-relaxed">{item.desc}</p>
                      {item.note && <p className="text-sm text-sagedeep mt-2 italic">{item.note}</p>}
                      {item.url && (
                        <a href={item.url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-copperdeep underline underline-offset-4 hover:text-copper"
                          data-testid={`local-link-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}>
                          Official info <ExternalLink size={13} aria-hidden />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm text-soft mt-6 italic">{content.local_guide.disclaimer}</p>
        </section>
      </main>

      {/* Rebook band */}
      <section className="mt-20 bg-charcoal text-cream" aria-label="Book again">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 text-center">
          <PebbleMark size={44} />
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold mt-4">{content.rebook.title}</h2>
          <p className="text-cream/75 text-lg mt-3 max-w-xl mx-auto">{content.rebook.body}</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <a href={p.booking_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center bg-copper text-cream font-extrabold rounded-full px-7 py-3.5 hover:bg-copperdeep transition-colors text-lg"
              data-testid="rebook-cta-button">
              {rebookLabel}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-creamdark border-t border-charcoal/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-8">
            <div>
              <Logo />
              <p className="text-soft mt-3 max-w-xs text-base">
                {p.address}, {p.city_state_zip}<br />Hosted by {p.host}
              </p>
              <div className="flex gap-2.5 mt-4">
                <a href={`tel:${p.phone_tel}`} className="p-3 bg-white border border-charcoal/10 rounded-full hover:border-copper transition-colors" aria-label={`Call the host at ${p.phone_display}`} data-testid="footer-call-button">
                  <Phone size={17} className="text-copper" />
                </a>
                <a href={`sms:${p.phone_sms}`} className="p-3 bg-white border border-charcoal/10 rounded-full hover:border-copper transition-colors" aria-label={`Text the host at ${p.phone_display}`} data-testid="footer-text-button">
                  <MessageSquareText size={17} className="text-copper" />
                </a>
              </div>
            </div>
            <div className="flex flex-col gap-2.5 sm:items-end">
              <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-charcoal hover:text-copperdeep transition-colors px-1 py-1" data-testid="guide-footer-booking-site">
                ← Back to the booking site
              </Link>
              <a href={p.facebook} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 bg-white border border-charcoal/10 rounded-full px-5 py-3 font-semibold text-charcoal hover:border-copper hover:text-copperdeep transition-colors"
                data-testid="footer-facebook-button">
                <Facebook size={17} aria-hidden /> Follow us on Facebook
              </a>
              <a href={p.instagram} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 bg-white border border-charcoal/10 rounded-full px-5 py-3 font-semibold text-charcoal hover:border-copper hover:text-copperdeep transition-colors"
                data-testid="footer-instagram-button">
                <Instagram size={17} aria-hidden /> Follow us on Instagram
              </a>
            </div>
          </div>
          <p className="text-xs text-soft mt-10">© {new Date().getFullYear()} {p.host} · Pebble Farms Guest Guide</p>
        </div>
      </footer>

      {/* Floating ask button */}
      <button
        onClick={() => setConciergeOpen(true)}
        className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 bg-copper text-cream font-extrabold rounded-full pl-4 pr-5 py-3.5 shadow-xl hover:bg-copperdeep hover:scale-105 transition-all"
        aria-label="Ask the Pebble Farms Concierge"
        data-testid="ai-concierge-trigger-button"
      >
        <MessageCircleQuestion size={20} aria-hidden /> Ask
      </button>

      <Concierge
        open={conciergeOpen}
        onClose={() => { setConciergeOpen(false); setConciergeSeed(null); }}
        suggested={content.concierge.suggested}
        onOpenPanel={(id) => openPanel(id)}
        phoneDisplay={p.phone_display}
        seed={conciergeSeed}
        key={conciergeSeed || "default"}
      />

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-charcoal/95 flex items-center justify-center"
            role="dialog" aria-modal="true" aria-label="Photo viewer"
            data-testid="gallery-lightbox"
            onClick={() => setLightbox(null)}
          >
            <button className="absolute top-4 right-4 p-3 text-cream hover:text-copper transition-colors" aria-label="Close photo viewer" data-testid="lightbox-close">
              <X size={26} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + content.photos.gallery.length) % content.photos.gallery.length); }}
              className="absolute left-3 sm:left-6 p-3 text-cream hover:text-copper transition-colors" aria-label="Previous photo" data-testid="lightbox-prev">
              <ChevronLeft size={30} />
            </button>
            <motion.figure
              key={lightbox}
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
              className="max-w-4xl w-full px-14" onClick={(e) => e.stopPropagation()}
            >
              <img src={content.photos.gallery[lightbox].url} alt={content.photos.gallery[lightbox].label}
                className="w-full max-h-[76vh] object-contain rounded-xl" />
              <figcaption className="text-center text-cream/85 mt-3 font-serif italic text-lg">
                {content.photos.gallery[lightbox].label}
              </figcaption>
            </motion.figure>
            <button
              onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % content.photos.gallery.length); }}
              className="absolute right-3 sm:right-6 p-3 text-cream hover:text-copper transition-colors" aria-label="Next photo" data-testid="lightbox-next">
              <ChevronRight size={30} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
